import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence, Reorder } from "framer-motion";
import {
  Package,
  Plus,
  Calendar,
  FileText,
  Download,
  Trash2,
  Edit3,
  Eye,
  Users,
  MapPin,
  Loader2,
  ChevronLeft,
  CalendarDays,
  Building2,
  GripVertical,
  Euro,
  Send,
  UserPlus,
  X,
  Printer,
  Search,
  Check,
  History,
  ChevronDown,
  Undo2,
  Filter,
} from "lucide-react";
import { DayPicker } from "react-day-picker";
import { it } from "date-fns/locale";
import { format } from "date-fns";
import "react-day-picker/style.css";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { useRequireAuth } from "../hooks/useAuth";
import { usePackagesStore } from "../stores/packagesStore";
import {
  usePackages,
  useCreatePackage,
  useUpdatePackage,
  useDeletePackage,
  useAddPackageItem,
  useDeletePackageItem,
  usePublishPackage,
  useUnpublishPackage,
  useReorderPackageItems,
  useSearchOperators,
  useInviteCollaborator,
  usePackageAuditLog,
} from "../hooks/usePackages";
import { cn } from "../lib/utils";
import type {
  Package as PackageType,
  PackageItem,
  PackageItemType,
  Operator,
  PackageAuditLog,
} from "../types/database";
import type { LucideIcon } from "lucide-react";

const itemTypeConfig: Record<
  PackageItemType,
  { label: string; color: string; icon: LucideIcon }
> = {
  event: { label: "Evento", color: "#284899", icon: CalendarDays },
  hotel: { label: "Hotel", color: "#3b82f6", icon: Building2 },
  activity: { label: "Attività", color: "#10b981", icon: MapPin },
  attraction: { label: "Attrazione", color: "#8b5cf6", icon: Eye },
  transport: { label: "Trasporto", color: "#6366f1", icon: MapPin },
  other: { label: "Altro", color: "#64748b", icon: Package },
};

export function Packages() {
  const { isLoading: authLoading } = useRequireAuth();

  // Store state
  const {
    selectedPackage,
    setSelectedPackage,
    currentPackageItems,
    setIsCreating,
    resetCurrentPackage,
  } = usePackagesStore();

  // Local state
  const [activeView, setActiveView] = useState<"list" | "create" | "detail">(
    "list"
  );
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    "all" | "draft" | "active" | "pending_approval" | "archived"
  >("all");

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    variant: "danger" | "warning";
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "",
    variant: "danger",
    onConfirm: () => { },
  });

  // Form state for new package
  const [newPackageData, setNewPackageData] = useState({
    name: "",
    description: "",
    start_date: "",
    end_date: "",
  });

  // Queries
  const { data: packages = [], isLoading: packagesLoading } = usePackages();

  // Mutations
  const createPackage = useCreatePackage();
  const deletePackage = useDeletePackage();
  const publishPackage = usePublishPackage();
  const unpublishPackage = useUnpublishPackage();

  // Stats
  const stats = useMemo(
    () => ({
      total: packages.length,
      draft: packages.filter((p) => p.status === "draft").length,
      active: packages.filter((p) => p.status === "active").length,
      pending: packages.filter((p) => p.status === "pending_approval").length,
    }),
    [packages]
  );

  // Handlers
  const handleCreatePackage = async () => {
    if (!newPackageData.name.trim()) return;

    try {
      const pkg = await createPackage.mutateAsync({
        name: newPackageData.name,
        description: newPackageData.description || undefined,
        start_date: newPackageData.start_date || undefined,
        end_date: newPackageData.end_date || undefined,
      });
      setNewPackageData({
        name: "",
        description: "",
        start_date: "",
        end_date: "",
      });
      setSelectedPackage(pkg);
      setActiveView("detail");
      setIsCreating(false);
    } catch (error) {
      console.error("Error creating package:", error);
    }
  };

  const handleDeletePackage = (id: string) => {
    setConfirmDialog({
      isOpen: true,
      title: "Elimina Pacchetto",
      message:
        "Sei sicuro di voler eliminare questo pacchetto? Questa azione non può essere annullata.",
      confirmText: "Elimina",
      variant: "danger",
      onConfirm: async () => {
        try {
          await deletePackage.mutateAsync(id);
          if (selectedPackage?.id === id) {
            resetCurrentPackage();
            setActiveView("list");
          }
        } catch (error) {
          console.error("Error deleting package:", error);
        }
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handlePublish = async (id: string) => {
    try {
      await publishPackage.mutateAsync(id);
    } catch (error) {
      console.error("Error publishing package:", error);
    }
  };

  const handleUnpublish = (id: string) => {
    setConfirmDialog({
      isOpen: true,
      title: "Annulla Pubblicazione",
      message:
        "Sei sicuro di voler annullare la pubblicazione? Il pacchetto tornerà in stato bozza.",
      confirmText: "Annulla Pubblicazione",
      variant: "warning",
      onConfirm: async () => {
        try {
          await unpublishPackage.mutateAsync(id);
        } catch (error) {
          console.error("Error unpublishing package:", error);
        }
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleGeneratePdf = () => {
    if (!selectedPackage) return;
    setShowPdfPreview(true);
  };

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header & Stats - Hidden in detail view */}
        {activeView !== "detail" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            >
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Package className="h-6 w-6 text-[#284899]" />
                  Pacchetti Turistici
                </h1>
                <p className="text-muted-foreground mt-1">
                  Crea e gestisci pacchetti turistici con calendario interattivo
                </p>
              </div>

              <Button
                onClick={() => {
                  setIsCreating(true);
                  setActiveView("create");
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Pacchetto
              </Button>
            </motion.div>

            {/* Stats - Clickable to filter */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                {
                  label: "Totali",
                  value: stats.total,
                  accent: "bg-slate-500",
                  textColor: "text-foreground",
                  filter: "all" as const,
                },
                {
                  label: "Bozze",
                  value: stats.draft,
                  accent: "bg-slate-400",
                  textColor: "text-muted-foreground",
                  filter: "draft" as const,
                },
                {
                  label: "Attivi",
                  value: stats.active,
                  accent: "bg-emerald-500",
                  textColor: "text-emerald-500",
                  filter: "active" as const,
                },
                {
                  label: "In Approvazione",
                  value: stats.pending,
                  accent: "bg-[#284899]",
                  textColor: "text-[#284899]",
                  filter: "pending_approval" as const,
                },
              ].map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="group cursor-pointer"
                  onClick={() => setStatusFilter(stat.filter)}
                >
                  <Card
                    className={cn(
                      "relative overflow-hidden transition-all duration-200 hover:scale-[1.02]",
                      statusFilter === stat.filter &&
                      "border-[#284899] shadow-lg shadow-[#284899]/20"
                    )}
                  >
                    <div
                      className={cn(
                        "absolute top-0 left-0 right-0 h-1 transition-all duration-200 group-hover:h-1.5",
                        stat.accent
                      )}
                    />
                    <CardContent className="p-5 pt-4">
                      <div className="space-y-1">
                        <span
                          className={cn(
                            "text-3xl font-bold tracking-tight",
                            stat.textColor
                          )}
                        >
                          {stat.value}
                        </span>
                        <p className="text-sm text-muted-foreground">
                          {stat.label}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Main Content */}
        <AnimatePresence mode="wait">
          {activeView === "list" && (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <PackagesList
                packages={packages}
                isLoading={packagesLoading}
                onSelect={(pkg) => {
                  setSelectedPackage(pkg);
                  setActiveView("detail");
                }}
                onDelete={handleDeletePackage}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
              />
            </motion.div>
          )}

          {activeView === "create" && (
            <motion.div
              key="create"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5 text-[#284899]" />
                    Crea Nuovo Pacchetto
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Nome Pacchetto *
                      </label>
                      <Input
                        placeholder="Es: Weekend in Toscana"
                        value={newPackageData.name}
                        onChange={(e) =>
                          setNewPackageData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Descrizione</label>
                      <Input
                        placeholder="Breve descrizione del pacchetto"
                        value={newPackageData.description}
                        onChange={(e) =>
                          setNewPackageData((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Data Inizio</label>
                      <Input
                        type="date"
                        value={newPackageData.start_date}
                        onChange={(e) =>
                          setNewPackageData((prev) => ({
                            ...prev,
                            start_date: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Data Fine</label>
                      <Input
                        type="date"
                        value={newPackageData.end_date}
                        onChange={(e) =>
                          setNewPackageData((prev) => ({
                            ...prev,
                            end_date: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsCreating(false);
                        setActiveView("list");
                      }}
                    >
                      Annulla
                    </Button>
                    <Button
                      onClick={handleCreatePackage}
                      disabled={
                        !newPackageData.name.trim() || createPackage.isPending
                      }
                    >
                      {createPackage.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      Crea Pacchetto
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeView === "detail" && selectedPackage && (
            <motion.div
              key="detail"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <PackageDetail
                package={selectedPackage}
                onBack={() => {
                  resetCurrentPackage();
                  setActiveView("list");
                }}
                onGeneratePdf={handleGeneratePdf}
                onPublish={() => handlePublish(selectedPackage.id)}
                onUnpublish={() => handleUnpublish(selectedPackage.id)}
                onDelete={() => handleDeletePackage(selectedPackage.id)}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* PDF Preview Modal */}
        <AnimatePresence>
          {showPdfPreview && selectedPackage && (
            <PdfPreviewModal
              package={selectedPackage}
              items={currentPackageItems}
              onClose={() => setShowPdfPreview(false)}
            />
          )}
        </AnimatePresence>

        {/* Confirm Dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          variant={confirmDialog.variant}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() =>
            setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
          }
        />
      </div>
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function PackagesList({
  packages,
  isLoading,
  onSelect,
  onDelete,
  statusFilter,
  onStatusFilterChange,
}: {
  packages: PackageType[];
  isLoading: boolean;
  onSelect: (pkg: PackageType) => void;
  onDelete: (id: string) => void;
  statusFilter: "all" | "draft" | "active" | "pending_approval" | "archived";
  onStatusFilterChange: (
    filter: "all" | "draft" | "active" | "pending_approval" | "archived"
  ) => void;
}) {
  const [searchQuery, setSearchQuery] = useState("");

  // Filter packages
  const filteredPackages = useMemo(() => {
    let result = packages;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (pkg) =>
          pkg.name.toLowerCase().includes(query) ||
          pkg.description?.toLowerCase().includes(query)
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      result = result.filter((pkg) => pkg.status === statusFilter);
    }

    return result;
  }, [packages, searchQuery, statusFilter]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Package className="h-16 w-16 text-muted-foreground opacity-30 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nessun pacchetto</h3>
          <p className="text-muted-foreground mb-4">
            Crea il tuo primo pacchetto turistico per iniziare
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <Input
            placeholder="Cerca pacchetti per nome..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<Search className="h-4 w-4" />}
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <select
            value={statusFilter}
            onChange={(e) =>
              onStatusFilterChange(e.target.value as typeof statusFilter)
            }
            className="h-10 rounded-lg border border-border bg-card pl-9 pr-3 text-sm focus:border-[#284899] focus:outline-none appearance-none cursor-pointer"
          >
            <option value="all">Tutti gli stati</option>
            <option value="draft">Bozze</option>
            <option value="active">Attivi</option>
            <option value="pending_approval">In Approvazione</option>
            <option value="archived">Archiviati</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      {/* Results count */}
      <p className="text-sm text-muted-foreground">
        {filteredPackages.length} pacchett
        {filteredPackages.length === 1 ? "o" : "i"}
        {(searchQuery || statusFilter !== "all") &&
          " trovat" + (filteredPackages.length === 1 ? "o" : "i")}
        {searchQuery && ` per "${searchQuery}"`}
      </p>

      {/* Package Grid */}
      {filteredPackages.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPackages.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <PackageCard
                package={pkg}
                onSelect={() => onSelect(pkg)}
                onDelete={() => onDelete(pkg.id)}
              />
            </motion.div>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Search className="h-12 w-12 text-muted-foreground opacity-30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nessun risultato</h3>
            <p className="text-muted-foreground mb-4">
              Nessun pacchetto corrisponde ai filtri selezionati
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery("");
                onStatusFilterChange("all");
              }}
            >
              Resetta filtri
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PackageCard({
  package: pkg,
  onSelect,
  onDelete,
}: {
  package: PackageType;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const statusConfig = {
    draft: { label: "Bozza", color: "bg-muted text-muted-foreground" },
    pending_approval: {
      label: "In Approvazione",
      color: "bg-[#284899]/20 text-[#1e3570]",
    },
    active: { label: "Attivo", color: "bg-emerald-500/20 text-emerald-600" },
    archived: { label: "Archiviato", color: "bg-muted text-muted-foreground" },
  };

  const status = statusConfig[pkg.status];
  const itemCount = pkg.items?.length || 0;
  const collaboratorCount =
    pkg.collaborators?.filter((c) => c.role !== "owner").length || 0;

  return (
    <Card className="group relative overflow-hidden transition-all duration-200 hover:scale-[1.01] hover:border-[#284899]/50">
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#284899] to-[#1e3570] opacity-0 group-hover:opacity-100 transition-opacity" />
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg group-hover:text-[#284899] transition-colors">
              {pkg.name}
            </h3>
            <Badge className={cn("mt-2 font-medium", status.color)}>
              {status.label}
            </Badge>
          </div>
          <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-[#284899]/10"
              onClick={onSelect}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {pkg.description && (
          <p className="text-sm text-muted-foreground mt-3 line-clamp-2 leading-relaxed">
            {pkg.description}
          </p>
        )}

        <div className="flex items-center flex-wrap gap-4 mt-4 text-xs">
          {(pkg.start_date || pkg.end_date) && (
            <span className="flex items-center gap-1.5 text-muted-foreground bg-secondary/50 px-2.5 py-1 rounded-full">
              <Calendar className="h-3.5 w-3.5 text-[#284899]" />
              {pkg.start_date && format(new Date(pkg.start_date), "dd/MM")}
              {pkg.start_date && pkg.end_date && " - "}
              {pkg.end_date && format(new Date(pkg.end_date), "dd/MM/yy")}
            </span>
          )}
          <span className="flex items-center gap-1.5 text-muted-foreground bg-secondary/50 px-2.5 py-1 rounded-full">
            <Package className="h-3.5 w-3.5 text-blue-500" />
            {itemCount} elementi
          </span>
          {collaboratorCount > 0 && (
            <span className="flex items-center gap-1.5 text-muted-foreground bg-secondary/50 px-2.5 py-1 rounded-full">
              <Users className="h-3.5 w-3.5 text-emerald-500" />
              {collaboratorCount}
            </span>
          )}
        </div>

        <div className="flex gap-2 mt-4 pt-4 border-t border-border/50">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 hover:border-[#284899]/50"
            onClick={onSelect}
          >
            <Edit3 className="h-3.5 w-3.5 mr-1.5" />
            Modifica
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PackageDetail({
  package: pkg,
  onBack,
  onGeneratePdf,
  onPublish,
  onUnpublish,
  onDelete,
}: {
  package: PackageType;
  onBack: () => void;
  onGeneratePdf: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
}) {
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItem, setNewItem] = useState<Partial<PackageItem>>({
    item_type: "event",
    name: "",
    scheduled_date: "",
  });
  const [orderedItems, setOrderedItems] = useState<PackageItem[]>([]);
  const [operatorSearch, setOperatorSearch] = useState("");
  const [selectedOperator, setSelectedOperator] = useState<Operator | null>(
    null
  );
  const [showOperatorResults, setShowOperatorResults] = useState(false);

  // Edit package state
  const [showEditModal, setShowEditModal] = useState(false);

  // Invite collaborator state
  const [showInviteModal, setShowInviteModal] = useState(false);

  const addPackageItem = useAddPackageItem();
  const deletePackageItem = useDeletePackageItem();
  const reorderItems = useReorderPackageItems();
  const updatePackage = useUpdatePackage();
  const inviteCollaborator = useInviteCollaborator();
  const { data: auditLog = [], isLoading: auditLoading } = usePackageAuditLog(
    pkg.id
  );

  // State for showing audit log
  const [showAuditLog, setShowAuditLog] = useState(false);
  const { data: searchResults = [], isLoading: searchLoading } =
    useSearchOperators(operatorSearch);

  // Confirm dialog state for item deletion
  const [itemConfirmDialog, setItemConfirmDialog] = useState<{
    isOpen: boolean;
    itemId: string;
  }>({
    isOpen: false,
    itemId: "",
  });

  const items = pkg.items || [];

  // Sync ordered items when package items change
  useEffect(() => {
    setOrderedItems(
      [...items].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    );
  }, [items]);

  const handleSelectOperator = (operator: Operator) => {
    setSelectedOperator(operator);
    // Determine item type based on typology
    const typologyLower = operator.typology?.toLowerCase() || "";
    let itemType: PackageItemType = "activity";
    if (
      typologyLower.includes("hotel") ||
      typologyLower.includes("accommodation") ||
      typologyLower.includes("resort")
    ) {
      itemType = "hotel";
    } else if (
      typologyLower.includes("attraction") ||
      typologyLower.includes("museum") ||
      typologyLower.includes("monument")
    ) {
      itemType = "attraction";
    } else if (
      typologyLower.includes("transport") ||
      typologyLower.includes("transfer") ||
      typologyLower.includes("shuttle")
    ) {
      itemType = "transport";
    } else if (
      typologyLower.includes("event") ||
      typologyLower.includes("festival") ||
      typologyLower.includes("show")
    ) {
      itemType = "event";
    }

    setNewItem((prev) => ({
      ...prev,
      name: operator.name,
      operator_id: operator.id,
      item_type: itemType,
    }));
    setOperatorSearch("");
    setShowOperatorResults(false);
  };

  const handleAddItem = async () => {
    if (!newItem.name?.trim()) return;

    try {
      await addPackageItem.mutateAsync({
        package_id: pkg.id,
        item_type: newItem.item_type || "event",
        name: newItem.name,
        description: newItem.description || null,
        scheduled_date: newItem.scheduled_date || null,
        scheduled_time: null,
        duration_minutes: null,
        price: null,
        notes: null,
        sort_order: items.length,
        operator_id: selectedOperator?.id || null,
        metadata: selectedOperator
          ? {
            operator_city: selectedOperator.city,
            operator_region: selectedOperator.region,
            operator_typology: selectedOperator.typology,
          }
          : {},
      });
      setNewItem({ item_type: "event", name: "", scheduled_date: "" });
      setSelectedOperator(null);
      setOperatorSearch("");
      setIsAddingItem(false);
    } catch (error) {
      console.error("Error adding item:", error);
    }
  };

  const handleDeleteItem = (itemId: string) => {
    setItemConfirmDialog({
      isOpen: true,
      itemId,
    });
  };

  const confirmDeleteItem = async () => {
    try {
      await deletePackageItem.mutateAsync({
        id: itemConfirmDialog.itemId,
        packageId: pkg.id,
      });
    } catch (error) {
      console.error("Error deleting item:", error);
    }
    setItemConfirmDialog({ isOpen: false, itemId: "" });
  };

  const statusConfig = {
    draft: { label: "Bozza", color: "bg-muted text-muted-foreground" },
    pending_approval: {
      label: "In Approvazione",
      color: "bg-[#284899]/20 text-[#1e3570]",
    },
    active: { label: "Attivo", color: "bg-emerald-500/20 text-emerald-600" },
    archived: { label: "Archiviato", color: "bg-muted text-muted-foreground" },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-xl font-bold">{pkg.name}</h2>
            <Badge className={cn("mt-1", statusConfig[pkg.status].color)}>
              {statusConfig[pkg.status].label}
            </Badge>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => setShowAuditLog(!showAuditLog)}
            >
              <History className="h-4 w-4 mr-2" />
              Storico Modifiche
              <ChevronDown
                className={cn(
                  "h-4 w-4 ml-2 transition-transform",
                  showAuditLog && "rotate-180"
                )}
              />
            </Button>
            <AnimatePresence>
              {showAuditLog && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden"
                >
                  <div className="p-3 border-b border-border bg-muted/30">
                    <h4 className="font-medium text-sm flex items-center gap-2">
                      <History className="h-4 w-4 text-[#284899]" />
                      Storico Modifiche
                    </h4>
                  </div>
                  <div className="p-3">
                    {auditLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : auditLog.length > 0 ? (
                      <div className="space-y-3 max-h-72 overflow-y-auto">
                        {auditLog.slice(0, 10).map((entry) => (
                          <AuditLogEntry key={entry.id} entry={entry} />
                        ))}
                        {auditLog.length > 10 && (
                          <p className="text-xs text-center text-muted-foreground pt-2">
                            + altre {auditLog.length - 10} modifiche
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted-foreground">
                        <History className="h-6 w-6 mx-auto mb-2 opacity-50" />
                        <p className="text-xs">Nessuna modifica registrata</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {pkg.status === "draft" && (
            <Button onClick={onPublish}>
              <Send className="h-4 w-4 mr-2" />
              Pubblica
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Package Info & Items */}
        <div className="lg:col-span-2 space-y-6">
          {/* Package Info */}
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-base">Dettagli Pacchetto</CardTitle>
              {pkg.status === "draft" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEditModal(true)}
                >
                  <Edit3 className="h-4 w-4 mr-1" />
                  Modifica
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {pkg.description ? (
                <p className="text-sm text-muted-foreground">
                  {pkg.description}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground/50 italic">
                  Nessuna descrizione
                </p>
              )}
              <div className="flex flex-wrap gap-4 text-sm">
                {pkg.start_date || pkg.end_date ? (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {pkg.start_date &&
                        format(new Date(pkg.start_date), "dd MMMM yyyy", {
                          locale: it,
                        })}
                      {pkg.start_date && pkg.end_date && " - "}
                      {pkg.end_date &&
                        format(new Date(pkg.end_date), "dd MMMM yyyy", {
                          locale: it,
                        })}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-muted-foreground/50">
                    <Calendar className="h-4 w-4" />
                    <span className="italic">Date non impostate</span>
                  </div>
                )}
                {pkg.total_price && (
                  <div className="flex items-center gap-2">
                    <Euro className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {pkg.total_price.toFixed(2)} {pkg.currency}
                    </span>
                  </div>
                )}
              </div>
              {/* Last modified info */}
              <div className="pt-2 border-t border-border mt-3">
                <p className="text-xs text-muted-foreground">
                  Ultima modifica:{" "}
                  {format(new Date(pkg.updated_at), "dd/MM/yyyy HH:mm", {
                    locale: it,
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-base">
                Elementi del Pacchetto
              </CardTitle>
              <Button size="sm" onClick={() => setIsAddingItem(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Aggiungi
              </Button>
            </CardHeader>
            <CardContent>
              {/* Add Item Form */}
              <AnimatePresence>
                {isAddingItem && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-4 p-4 rounded-xl border border-[#284899]/50 bg-[#284899]/5"
                  >
                    {/* Operator Search */}
                    <div className="mb-4">
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                        <Search className="h-3 w-3" />
                        Cerca operatore dal database
                      </label>
                      <div className="relative">
                        <Input
                          placeholder="Cerca per nome o città..."
                          value={operatorSearch}
                          onChange={(e) => {
                            setOperatorSearch(e.target.value);
                            setShowOperatorResults(e.target.value.length >= 2);
                          }}
                          onFocus={() =>
                            operatorSearch.length >= 2 &&
                            setShowOperatorResults(true)
                          }
                        />
                        {showOperatorResults && (
                          <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-lg border border-border bg-card"
                          >
                            {searchLoading ? (
                              <div className="p-3 text-center text-sm text-muted-foreground">
                                Ricerca...
                              </div>
                            ) : searchResults.length === 0 ? (
                              <div className="p-3 text-center text-sm text-muted-foreground">
                                Nessun risultato
                              </div>
                            ) : (
                              searchResults.map((op) => (
                                <button
                                  key={op.id}
                                  type="button"
                                  className="w-full px-3 py-2 text-left hover:bg-secondary/50 transition-colors"
                                  onClick={() => handleSelectOperator(op)}
                                >
                                  <div className="font-medium text-sm truncate">
                                    {op.name}
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <MapPin className="h-3 w-3" />
                                    {op.city} -{" "}
                                    <span className="text-[#284899]">
                                      {op.typology}
                                    </span>
                                  </div>
                                </button>
                              ))
                            )}
                          </motion.div>
                        )}
                      </div>
                      {selectedOperator && (
                        <div className="mt-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-emerald-500" />
                              <span className="text-sm font-medium">
                                {selectedOperator.name}
                              </span>
                              <Badge className="bg-emerald-500/20 text-emerald-600 text-[10px]">
                                Da Database
                              </Badge>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => {
                                setSelectedOperator(null);
                                setNewItem((prev) => ({
                                  ...prev,
                                  name: "",
                                  operator_id: undefined,
                                }));
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {selectedOperator.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {selectedOperator.city}
                              </span>
                            )}
                            {selectedOperator.typology && (
                              <span className="text-[#284899]">
                                {selectedOperator.typology}
                              </span>
                            )}
                          </div>
                          {/* Data programmazione per operatore da database */}
                          <div className="mt-3 pt-3 border-t border-emerald-500/20">
                            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                              Data programmazione
                            </label>
                            <Input
                              type="date"
                              value={newItem.scheduled_date || ""}
                              onChange={(e) =>
                                setNewItem((prev) => ({
                                  ...prev,
                                  scheduled_date: e.target.value,
                                }))
                              }
                              className="bg-background"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Sezione manuale - mostrata solo se non c'è operatore selezionato */}
                    {!selectedOperator && (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="h-px flex-1 bg-border" />
                          <span className="text-xs text-muted-foreground">
                            oppure inserisci manualmente
                          </span>
                          <div className="h-px flex-1 bg-border" />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <select
                            value={newItem.item_type}
                            onChange={(e) =>
                              setNewItem((prev) => ({
                                ...prev,
                                item_type: e.target.value as PackageItemType,
                              }))
                            }
                            className="h-9 rounded-lg border border-border bg-card px-3 text-sm focus:border-[#284899] focus:outline-none"
                          >
                            {Object.entries(itemTypeConfig).map(
                              ([key, config]) => (
                                <option key={key} value={key}>
                                  {config.label}
                                </option>
                              )
                            )}
                          </select>
                          <Input
                            placeholder="Nome elemento"
                            value={newItem.name || ""}
                            onChange={(e) =>
                              setNewItem((prev) => ({
                                ...prev,
                                name: e.target.value,
                              }))
                            }
                          />
                          <Input
                            type="date"
                            value={newItem.scheduled_date || ""}
                            onChange={(e) =>
                              setNewItem((prev) => ({
                                ...prev,
                                scheduled_date: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </>
                    )}
                    <div className="flex gap-2 mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setIsAddingItem(false);
                          setSelectedOperator(null);
                        }}
                      >
                        Annulla
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleAddItem}
                        disabled={!newItem.name?.trim()}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Aggiungi
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Items List with Drag & Drop */}
              {orderedItems.length > 0 ? (
                <Reorder.Group
                  axis="y"
                  values={orderedItems}
                  onReorder={(newOrder) => {
                    setOrderedItems(newOrder);
                    // Debounce the API call
                    reorderItems.mutate({ packageId: pkg.id, items: newOrder });
                  }}
                  className="space-y-2"
                >
                  {orderedItems.map((item) => {
                    const config = itemTypeConfig[item.item_type];
                    const Icon = config.icon;
                    return (
                      <Reorder.Item
                        key={item.id}
                        value={item}
                        className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30 border border-border group cursor-grab active:cursor-grabbing"
                        whileDrag={{
                          scale: 1.02,
                          boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                          backgroundColor: "hsl(var(--card))",
                        }}
                      >
                        <GripVertical className="h-4 w-4 text-muted-foreground opacity-50" />
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${config.color}40` }}
                        >
                          <Icon
                            className="h-4 w-4"
                            style={{ color: config.color }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{item.name}</p>
                            {item.operator_id && (
                              <Badge className="bg-emerald-500/15 text-emerald-600 text-[10px] px-1.5 py-0">
                                Database
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{config.label}</span>
                            {item.scheduled_date && (
                              <>
                                <span>•</span>
                                <span>
                                  {format(
                                    new Date(item.scheduled_date),
                                    "dd/MM/yyyy"
                                  )}
                                </span>
                              </>
                            )}
                            {item.operator_id &&
                              (item.metadata as any)?.operator_city && (
                                <>
                                  <span>•</span>
                                  <span className="flex items-center gap-0.5">
                                    <MapPin className="h-2.5 w-2.5" />
                                    {(item.metadata as any).operator_city}
                                  </span>
                                </>
                              )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteItem(item.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </Reorder.Item>
                    );
                  })}
                </Reorder.Group>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nessun elemento aggiunto</p>
                  <p className="text-xs">
                    Aggiungi eventi, hotel e attività al pacchetto
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Calendar & Collaborators */}
        <div className="space-y-6">
          {/* Mini Calendar */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4 text-[#284899]" />
                Calendario
              </CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center">
              <DayPicker
                mode="single"
                locale={it}
                numberOfMonths={1}
                showOutsideDays
                modifiers={{
                  hasItem: items
                    .filter((item) => item.scheduled_date)
                    .map((item) => new Date(item.scheduled_date!)),
                }}
                modifiersStyles={{
                  hasItem: {
                    backgroundColor: "rgba(245, 158, 11, 0.2)",
                    borderRadius: "50%",
                  },
                }}
              />
            </CardContent>
          </Card>

          {/* Collaborators */}
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-[#284899]" />
                Collaboratori
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInviteModal(true)}
                disabled={pkg.status === "archived"}
              >
                <UserPlus className="h-4 w-4 mr-1" />
                Invita
              </Button>
            </CardHeader>
            <CardContent>
              {pkg.collaborators && pkg.collaborators.length > 0 ? (
                <div className="space-y-2">
                  {pkg.collaborators.map((collab) => (
                    <div
                      key={collab.id}
                      className="flex items-center gap-3 p-2 rounded-lg bg-secondary/30"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#284899]/20 flex items-center justify-center">
                        <Users className="h-4 w-4 text-[#284899]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">
                          {collab.user?.first_name} {collab.user?.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {collab.role}
                        </p>
                      </div>
                      <Badge
                        variant="secondary"
                        className={cn(
                          collab.status === "accepted" &&
                          "bg-emerald-500/20 text-emerald-600",
                          collab.status === "pending" &&
                          "bg-[#284899]/20 text-[#1e3570]",
                          collab.status === "rejected" &&
                          "bg-red-500/20 text-red-600"
                        )}
                      >
                        {collab.status === "accepted"
                          ? "Confermato"
                          : collab.status === "pending"
                            ? "In attesa"
                            : "Rifiutato"}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nessun collaboratore</p>
                  <p className="text-xs">
                    Invita altri operatori per pacchetti congiunti
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardContent className="p-4 space-y-2">
              {pkg.status === "draft" && (
                <Button className="w-full justify-start" onClick={onPublish}>
                  <Send className="h-4 w-4 mr-2" />
                  Pubblica Pacchetto
                </Button>
              )}
              {(pkg.status === "active" ||
                pkg.status === "pending_approval") && (
                  <Button
                    variant="outline"
                    className="w-full justify-start text-[#1e3570] hover:text-[#15254d] hover:bg-[#284899]/10"
                    onClick={onUnpublish}
                  >
                    <Undo2 className="h-4 w-4 mr-2" />
                    Annulla Pubblicazione
                  </Button>
                )}
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={onGeneratePdf}
              >
                <Download className="h-4 w-4 mr-2" />
                Scarica PDF
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-500/10"
                onClick={onDelete}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Elimina Pacchetto
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Package Modal */}
      <AnimatePresence>
        {showEditModal && (
          <EditPackageModal
            package={pkg}
            onSave={async (updates) => {
              await updatePackage.mutateAsync({ id: pkg.id, ...updates });
              setShowEditModal(false);
            }}
            onClose={() => setShowEditModal(false)}
            isSaving={updatePackage.isPending}
          />
        )}
      </AnimatePresence>

      {/* Invite Collaborator Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <InviteCollaboratorModal
            packageId={pkg.id}
            packageName={pkg.name}
            existingCollaborators={pkg.collaborators || []}
            onInvite={async (userId) => {
              await inviteCollaborator.mutateAsync({
                packageId: pkg.id,
                userId,
              });
              setShowInviteModal(false);
            }}
            onClose={() => setShowInviteModal(false)}
            isInviting={inviteCollaborator.isPending}
          />
        )}
      </AnimatePresence>

      {/* Confirm Delete Item Dialog */}
      <ConfirmDialog
        isOpen={itemConfirmDialog.isOpen}
        title="Elimina Elemento"
        message="Sei sicuro di voler eliminare questo elemento dal pacchetto?"
        confirmText="Elimina"
        variant="danger"
        onConfirm={confirmDeleteItem}
        onCancel={() => setItemConfirmDialog({ isOpen: false, itemId: "" })}
      />
    </div>
  );
}

function PdfPreviewModal({
  package: pkg,
  items,
  onClose,
}: {
  package: PackageType;
  items: PackageItem[];
  onClose: () => void;
}) {
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const itemsHtml = items
      .map((item) => {
        const config = itemTypeConfig[item.item_type];
        return `
        <div style="display: flex; align-items: flex-start; gap: 12px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; margin-bottom: 8px;">
          <div style="width: 40px; height: 40px; background: ${config.color
          }20; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
            <span style="font-size: 18px;">${item.item_type === "event"
            ? "📅"
            : item.item_type === "hotel"
              ? "🏨"
              : item.item_type === "activity"
                ? "🎯"
                : item.item_type === "attraction"
                  ? "🏛️"
                  : "📦"
          }</span>
          </div>
          <div>
            <h4 style="margin: 0; font-size: 14px; font-weight: 600;">${item.name
          }</h4>
            <p style="margin: 4px 0 0; font-size: 12px; color: #6b7280;">
              ${config.label}${item.scheduled_date
            ? ` • ${format(new Date(item.scheduled_date), "dd MMMM yyyy", {
              locale: it,
            })}`
            : ""
          }
            </p>
            ${item.description
            ? `<p style="margin: 8px 0 0; font-size: 12px; color: #374151;">${item.description}</p>`
            : ""
          }
          </div>
        </div>
      `;
      })
      .join("");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${pkg.name} - DPM Pacchetto Turistico</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: system-ui, -apple-system, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { font-size: 24px; color: #284899; margin-bottom: 8px; }
            h2 { font-size: 18px; color: #374151; margin: 24px 0 12px; }
            .header { border-bottom: 2px solid #284899; padding-bottom: 20px; margin-bottom: 24px; }
            .meta { display: flex; gap: 24px; color: #6b7280; font-size: 14px; margin-top: 12px; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px; }
            @media print {
              body { padding: 20px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${pkg.name}</h1>
            ${pkg.description
        ? `<p style="color: #6b7280; margin-top: 8px;">${pkg.description}</p>`
        : ""
      }
            <div class="meta">
              ${pkg.start_date || pkg.end_date
        ? `<span>📅 ${pkg.start_date
          ? format(new Date(pkg.start_date), "dd MMMM yyyy", {
            locale: it,
          })
          : ""
        } ${pkg.start_date && pkg.end_date ? "-" : ""} ${pkg.end_date
          ? format(new Date(pkg.end_date), "dd MMMM yyyy", {
            locale: it,
          })
          : ""
        }</span>`
        : ""
      }
              <span>📦 ${items.length} elementi</span>
            </div>
          </div>
          
          <h2>Programma</h2>
          ${itemsHtml ||
      '<p style="color: #9ca3af; padding: 20px; text-align: center;">Nessun elemento nel pacchetto</p>'
      }
          
          <div class="footer">
            <p>Generato da DPM - Destination Plan Machine</p>
            <p>${format(new Date(), "dd MMMM yyyy, HH:mm", { locale: it })}</p>
          </div>
          
          <script>window.print();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#284899]" />
            Anteprima PDF
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-white">
          {/* PDF Preview Content */}
          <div className="border-b-2 border-[#284899] pb-4 mb-6">
            <h1 className="text-2xl font-bold text-[#284899]">{pkg.name}</h1>
            {pkg.description && (
              <p className="text-gray-600 mt-2">{pkg.description}</p>
            )}
            <div className="flex gap-6 text-gray-500 text-sm mt-3">
              {(pkg.start_date || pkg.end_date) && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {pkg.start_date &&
                    format(new Date(pkg.start_date), "dd MMMM yyyy", {
                      locale: it,
                    })}
                  {pkg.start_date && pkg.end_date && " - "}
                  {pkg.end_date &&
                    format(new Date(pkg.end_date), "dd MMMM yyyy", {
                      locale: it,
                    })}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Package className="h-4 w-4" />
                {items.length} elementi
              </span>
            </div>
          </div>

          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Programma
          </h2>

          {items.length > 0 ? (
            <div className="space-y-3">
              {items.map((item) => {
                const config = itemTypeConfig[item.item_type];
                const Icon = config.icon;
                return (
                  <div
                    key={item.id}
                    className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg"
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${config.color}40` }}
                    >
                      <Icon
                        className="h-5 w-5"
                        style={{ color: config.color }}
                      />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm text-gray-800">
                        {item.name}
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {config.label}
                        {item.scheduled_date &&
                          ` • ${format(
                            new Date(item.scheduled_date),
                            "dd MMMM yyyy",
                            { locale: it }
                          )}`}
                      </p>
                      {item.description && (
                        <p className="text-xs text-gray-600 mt-2">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-gray-400 py-8">
              Nessun elemento nel pacchetto
            </p>
          )}

          <div className="mt-8 pt-4 border-t border-gray-200 text-center text-gray-400 text-xs">
            <p>Generato da DPM - Destination Plan Machine</p>
            <p>{format(new Date(), "dd MMMM yyyy, HH:mm", { locale: it })}</p>
          </div>
        </div>

        <div className="p-4 border-t border-border flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Chiudi
          </Button>
          <Button className="flex-1" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Stampa / Scarica PDF
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// EDIT PACKAGE MODAL
// ============================================

function EditPackageModal({
  package: pkg,
  onSave,
  onClose,
  isSaving,
}: {
  package: PackageType;
  onSave: (updates: {
    name: string;
    description?: string;
    start_date?: string;
    end_date?: string;
  }) => Promise<void>;
  onClose: () => void;
  isSaving: boolean;
}) {
  const [formData, setFormData] = useState({
    name: pkg.name,
    description: pkg.description || "",
    start_date: pkg.start_date || "",
    end_date: pkg.end_date || "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    await onSave({
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      start_date: formData.start_date || undefined,
      end_date: formData.end_date || undefined,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Edit3 className="h-5 w-5 text-[#284899]" />
            Modifica Pacchetto
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Nome Pacchetto *</label>
            <Input
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Es: Weekend in Toscana"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Descrizione</label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Breve descrizione del pacchetto..."
              className="w-full h-24 rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:border-[#284899] focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Data Inizio</label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Data Fine</label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, end_date: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isSaving || !formData.name.trim()}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Salva Modifiche
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// INVITE COLLABORATOR MODAL
// ============================================

// ============================================
// AUDIT LOG ENTRY
// ============================================

const auditActionLabels: Record<
  string,
  { label: string; icon: typeof History; color: string }
> = {
  created: { label: "Pacchetto creato", icon: Plus, color: "text-emerald-500" },
  updated: { label: "Modificato", icon: Edit3, color: "text-blue-500" },
  published: { label: "Pubblicato", icon: Send, color: "text-emerald-500" },
  archived: {
    label: "Archiviato",
    icon: Package,
    color: "text-muted-foreground",
  },
  item_added: {
    label: "Elemento aggiunto",
    icon: Plus,
    color: "text-[#284899]",
  },
  item_removed: {
    label: "Elemento rimosso",
    icon: Trash2,
    color: "text-red-500",
  },
  item_updated: {
    label: "Elemento modificato",
    icon: Edit3,
    color: "text-blue-500",
  },
  collaborator_invited: {
    label: "Collaboratore invitato",
    icon: UserPlus,
    color: "text-purple-500",
  },
  collaborator_responded: {
    label: "Risposta collaboratore",
    icon: Check,
    color: "text-emerald-500",
  },
};

const fieldLabels: Record<string, string> = {
  name: "Nome",
  description: "Descrizione",
  start_date: "Data inizio",
  end_date: "Data fine",
  status: "Stato",
};

function AuditLogEntry({ entry }: { entry: PackageAuditLog }) {
  const config = auditActionLabels[entry.action] || {
    label: entry.action,
    icon: History,
    color: "text-muted-foreground",
  };
  const Icon = config.icon;
  const userName = entry.user
    ? `${entry.user.first_name || ""} ${entry.user.last_name || ""}`.trim() ||
    "Utente"
    : "Sistema";

  const getDescription = () => {
    if (entry.field_changed) {
      const fieldLabel =
        fieldLabels[entry.field_changed] || entry.field_changed;
      return `${fieldLabel} modificato`;
    }

    if (entry.action === "item_added" && entry.details) {
      const details = entry.details as {
        item_name?: string;
        item_type?: string;
      };
      return details.item_name || "Nuovo elemento";
    }

    if (entry.action === "item_removed" && entry.details) {
      const details = entry.details as { item_name?: string };
      return details.item_name || "Elemento rimosso";
    }

    if (entry.action === "collaborator_invited" && entry.details) {
      const details = entry.details as { invited_user_name?: string };
      return details.invited_user_name || "Nuovo collaboratore";
    }

    if (entry.action === "collaborator_responded" && entry.details) {
      const details = entry.details as {
        response?: string;
        user_name?: string;
      };
      return details.response === "accepted"
        ? "Invito accettato"
        : "Invito rifiutato";
    }

    return config.label;
  };

  return (
    <div className="flex items-start gap-2 text-xs">
      <div className={cn("mt-0.5", config.color)}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground truncate">
          {getDescription()}
        </p>
        <p className="text-muted-foreground">
          {userName} •{" "}
          {format(new Date(entry.created_at), "dd/MM HH:mm", { locale: it })}
        </p>
      </div>
    </div>
  );
}

function InviteCollaboratorModal({
  packageName,
  existingCollaborators,
  onInvite,
  onClose,
  isInviting,
}: {
  packageId: string;
  packageName: string;
  existingCollaborators: PackageType["collaborators"];
  onInvite: (userId: string) => Promise<void>;
  onClose: () => void;
  isInviting: boolean;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);
  const [showResults, setShowResults] = useState(false);

  // Use operator search to find users (operators have user accounts)
  const { data: searchResults = [], isLoading: searchLoading } =
    useSearchOperators(searchQuery, 20);

  // Filter out existing collaborators
  const existingIds = new Set(
    (existingCollaborators || []).map((c) => c.user_id)
  );
  const filteredResults = searchResults.filter((op) => !existingIds.has(op.id));

  const handleSelectUser = (operator: Operator) => {
    setSelectedUser({
      id: operator.id,
      name: operator.name,
      email: operator.email || "",
    });
    setSearchQuery("");
    setShowResults(false);
  };

  const handleInvite = async () => {
    if (!selectedUser) return;
    await onInvite(selectedUser.id);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-[#284899]" />
            Invita Collaboratore
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Invita un operatore a collaborare su "{packageName}"
          </p>
        </div>

        <div className="p-6 space-y-4">
          {/* Search */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              Cerca operatore
            </label>
            <div className="relative">
              <Input
                placeholder="Cerca per nome o città..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowResults(e.target.value.length >= 2);
                }}
                onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
              />
              {showResults && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-lg border border-border bg-card shadow-lg"
                >
                  {searchLoading ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                      Ricerca in corso...
                    </div>
                  ) : filteredResults.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      Nessun operatore trovato
                    </div>
                  ) : (
                    filteredResults.map((op) => (
                      <button
                        key={op.id}
                        type="button"
                        className="w-full px-4 py-3 text-left hover:bg-secondary/50 transition-colors border-b border-border last:border-b-0"
                        onClick={() => handleSelectUser(op)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#284899]/20 flex items-center justify-center">
                            <Building2 className="h-4 w-4 text-[#284899]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {op.name}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {[op.city, op.region].filter(Boolean).join(", ")}
                              {op.typology && (
                                <span className="text-[#284899] ml-1">
                                  • {op.typology}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </motion.div>
              )}
            </div>
          </div>

          {/* Selected User */}
          {selectedUser && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Check className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="font-medium">{selectedUser.name}</p>
                    {selectedUser.email && (
                      <p className="text-xs text-muted-foreground">
                        {selectedUser.email}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setSelectedUser(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Info */}
          <div className="p-3 rounded-lg bg-secondary/30 border border-border">
            <p className="text-xs text-muted-foreground">
              <strong className="text-foreground">Nota:</strong> L'operatore
              invitato riceverà una notifica e dovrà accettare l'invito. Il
              pacchetto non potrà essere pubblicato finché tutti i collaboratori
              non avranno accettato.
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-border flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Annulla
          </Button>
          <Button
            className="flex-1"
            onClick={handleInvite}
            disabled={!selectedUser || isInviting}
          >
            {isInviting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Invia Invito
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
